//--------------------------------------------------------------------//
//******************** providerAccountsTab *******************************//
//--------------------------------------------------------------------//
// Lists every email_accounts row under this provider, across ALL users
// (mirrors trunk-numbers.tab.js's DID-number-to-GudHub-user mapping, which
// is also admin-style rather than viewer-scoped) - not a slice of the
// parent `provider` document like did_numbers is, since accounts live in
// their own collection/endpoint, so this tab fetches its own data instead
// of receiving it via binding.
//
// "+ Add Account": for a generic_imap provider, opens a dialog to pick any
// GudHub user (gh-user-selecting, same pattern as trunk-numbers.tab.js's
// openNumberDialog) plus mailbox credentials. For a google provider, OAuth
// requires the account owner's own live consent, so it just starts the
// Connect-Gmail popup for the CURRENT session's user - it cannot provision
// a Gmail/Workspace account on someone else's behalf even from this
// admin-style view.
import providerAccountsTemplateUrl from './providerAccounts.template.html';

angular.module('emailSettingsModule')

.directive('providerAccountsTab', ['EmailSettingsService', 'GhDialog', function (EmailSettingsService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            provider: '='
        },
        controller: ['$scope', '$sce', 'localizationService', function ($scope, $sce, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            $scope.accounts = [];
            $scope.loading = true;

            // gh-user-selecting reads ghField.data_model directly - a stable
            // stub avoids both a runtime error and a fresh-object-every-
            // digest infinite $digest loop an inline template literal would
            // cause (same reasoning as trunk-numbers.tab.js's ghFieldStub).
            $scope.ghFieldStub = { data_model: { photo_size: '32px' } };

            // Signatures are self-authored HTML the account owner saved for
            // their own mailbox - trusted the same way this codebase already
            // trusts other self-authored rich text (gh_quiz.js/tinymce.js's
            // $sce.trustAsHtml; ngSanitize isn't loaded here).
            $scope.trustHtml = function (html) {
                return $sce.trustAsHtml(html || '');
            };

            const safeApply = function () {
                $scope.$applyAsync();
            };

            $scope.refreshAccounts = async function () {
                try {
                    $scope.accounts = await EmailSettingsService.getAccountsByProvider($scope.provider.provider_id);
                } catch (error) {
                    console.log('Load provider accounts error:', error);
                } finally {
                    $scope.loading = false;
                    safeApply();
                }
            };

            // One dialog for both "Add Account" and "click a row to edit it"
            // (mirrors trunk-numbers.tab.js's openNumberDialog, which is
            // also a single function serving both add and edit, switching
            // on whether an existing item was passed in) - so the two are
            // visually and structurally identical, not just similar. Only
            // the parts that genuinely differ between the two are gated by
            // `isEditing`: the title/button text, and the sync status/Test
            // Connection/signature-editor/Delete sections, which only make
            // sense once an account_id exists to act on. Nothing here
            // auto-persists mid-dialog (including Receive/Send, unlike the
            // switch-based version this replaced) - the whole form commits
            // together on Save/Add, exactly like the add flow already did.
            // * existingAccount (Object, optional) - the account being edited, or
            //   omitted/null to create a new one.
            $scope.openAccountFormDialog = function (existingAccount) {
                const isEditing = !!existingAccount;
                const outerScope = $scope;
                const providerId = $scope.provider.provider_id;
                const showCredentials = $scope.provider.type === 'generic_imap';

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="add_email_account-popup" style="padding: 20px; position: relative; min-width: 420px; max-height: 80vh; overflow-y: auto;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #333;">{{isEditing ? translate('Edit Email Account') : translate('Add Email Account')}}</h3>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('GudHub user')}}</label>
                                <div class="did_number-user_picker" ng-style="fieldErrors.user ? {'border': '1px solid #f44336', 'border-radius': '8px', 'padding': '4px'} : {}" style="margin-bottom: 16px;">
                                    <gh-user-selecting gh-model="gudhubUserId" gh-field="ghFieldStub" gh-mode="little_photo" gh-dropdown="" editable="true"></gh-user-selecting>
                                </div>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Email Address')}}</label>
                                <input type="email" ng-model="account.email_address" ng-style="fieldErrors.email ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 15px; outline: none; margin-bottom: 12px;">

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Display Name')}}</label>
                                <input type="text" ng-model="account.display_name" placeholder="{{translate('Optional')}}" style="width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 15px; outline: none; margin-bottom: 16px;">

                                <p ng-if="showCredentials" style="font-size: 12px; color: #a4a4a8; margin-bottom: 12px;">{{translate('Host/port come from this provider\\'s Connection settings (Provider Info tab) - only credentials are needed per account.')}}</p>

                                <div style="display: flex; gap: 20px; margin-bottom: 16px;">
                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 13px; color: #454245; cursor: pointer;">
                                        <input type="checkbox" ng-model="receivingEnabled">
                                        {{translate('Receive emails')}}
                                    </label>
                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 13px; color: #454245; cursor: pointer;">
                                        <input type="checkbox" ng-model="sendingEnabled">
                                        {{translate('Send emails')}}
                                    </label>
                                </div>

                                <div ng-if="showCredentials && receivingEnabled">
                                    <div style="font-size: 12px; font-weight: 600; color: #a4a4a8; text-transform: uppercase; margin-bottom: 8px;">{{translate('IMAP (receiving)')}}</div>
                                    <div style="display: flex; gap: 8px; margin-bottom: 16px;">
                                        <input type="text" ng-model="account.imap.username" placeholder="{{translate('Username')}}" ng-style="fieldErrors.imapUsername ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="flex: 1; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 14px; outline: none;">
                                        <input type="password" ng-model="account.imap.password" placeholder="{{isEditing ? translate('Leave blank to keep current') : translate('Password')}}" ng-style="fieldErrors.imapPassword ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="flex: 1; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 14px; outline: none;">
                                    </div>
                                </div>

                                <div ng-if="showCredentials && sendingEnabled">
                                    <div style="font-size: 12px; font-weight: 600; color: #a4a4a8; text-transform: uppercase; margin-bottom: 8px;">{{translate('SMTP (sending)')}}</div>
                                    <div style="display: flex; gap: 8px; margin-bottom: 16px;">
                                        <input type="text" ng-model="account.smtp.username" placeholder="{{translate('Username')}}" ng-style="fieldErrors.smtpUsername ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="flex: 1; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 14px; outline: none;">
                                        <input type="password" ng-model="account.smtp.password" placeholder="{{isEditing ? translate('Leave blank to keep current') : translate('Password')}}" ng-style="fieldErrors.smtpPassword ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="flex: 1; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 14px; outline: none;">
                                    </div>
                                </div>

                                <div ng-if="isEditing">
                                    <label style="display: flex; align-items: center; gap: 6px; font-size: 13px; color: #454245; margin-bottom: 12px;">
                                        <input type="checkbox" ng-model="account.default_email_account" ng-disabled="account.default_email_account">
                                        {{account.default_email_account ? translate('Default account for sending') : translate('Make this the default account for sending')}}
                                    </label>

                                    <p style="font-size: 12px; color: #a4a4a8; margin-bottom: 12px;">
                                        {{existingAccount.sync_state.last_synced_at ? translate('Last synced') + ' ' + (existingAccount.sync_state.last_synced_at | date:'short') : translate('Not synced yet')}}
                                    </p>

                                    <div ng-if="existingAccount.sync_state.last_error" style="margin-bottom: 16px; padding: 8px 10px; border-radius: 6px; background: #fdecea; color: #b3261e; font-size: 12px;">{{existingAccount.sync_state.last_error}}</div>

                                    <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
                                        <button class="btn btn-blue-reverse" ng-click="testConnection()" ng-disabled="existingAccount._testing">
                                            {{existingAccount._testing ? translate('Testing...') : translate('Test Connection')}}
                                        </button>
                                        <span ng-if="existingAccount._testResult" ng-style="{color: existingAccount._testResult.success ? '#05B52C' : '#FF0D0D'}" style="font-size: 13px;">
                                            {{existingAccount._testResult.message}}
                                        </span>
                                    </div>

                                    <div class="email_accounts-signature">
                                        <div ng-if="!existingAccount._editingSignature" class="email_accounts-signature_preview" ng-click="toggleSignatureEditor()">
                                            <span ng-if="existingAccount.signature_html" ng-bind-html="trustHtml(existingAccount.signature_html)"></span>
                                            <span ng-if="!existingAccount.signature_html" class="placeholder">{{translate('Click to add a signature')}}</span>
                                        </div>

                                        <div ng-if="existingAccount._editingSignature" class="email_accounts-signature_editor">
                                            <div class="email_accounts-signature_editor_actions">
                                                <button class="btn btn-grean small" ng-click="saveSignature()" ng-disabled="existingAccount._savingSignature">
                                                    {{existingAccount._savingSignature ? translate('Saving...') : translate('Save')}}
                                                </button>
                                                <button class="btn btn-blue-reverse small" ng-click="existingAccount._editingSignature = false">{{translate('Cancel')}}</button>
                                            </div>
                                            <email-signature-editor ng-model="existingAccount._signatureDraft"></email-signature-editor>
                                        </div>
                                    </div>
                                </div>

                                <div ng-show="errMessage" style="color: #f44336; font-size: 13px; font-weight: 600; margin-bottom: 12px; padding: 8px 10px; background: #fdecea; border-radius: 6px;">{{errMessage}}</div>

                                <div style="display: flex; gap: 10px; justify-content: center; margin-top: 20px;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-blue" ng-click="save()" ng-disabled="saving">{{isEditing ? translate('Save') : translate('Add')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: { providerId, existingAccount, isEditing, showCredentials, outerScope },
                    controller: ['$scope', 'providerId', 'existingAccount', 'isEditing', 'showCredentials', 'outerScope', 'localizationService', function ($scope, providerId, existingAccount, isEditing, showCredentials, outerScope, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);

                        $scope.isEditing = isEditing;
                        $scope.showCredentials = showCredentials;
                        $scope.existingAccount = existingAccount;
                        $scope.ghFieldStub = { data_model: { photo_size: '48px' } };
                        $scope.trustHtml = outerScope.trustHtml;

                        $scope.gudhubUserId = isEditing ? String(existingAccount.gudhub_user_id) : '';
                        $scope.receivingEnabled = isEditing ? !!existingAccount.receiving_enabled : true;
                        $scope.sendingEnabled = isEditing ? !!existingAccount.sending_enabled : true;

                        // A draft copy, not the live table row - nothing here
                        // touches the real account until Save/Add succeeds
                        // (see the note above the dialog). The real password
                        // is never returned by the API (write-only), so the
                        // password fields always start blank even when
                        // editing - blank on save means "keep the current one"
                        // (see EmailDatabase.updateAccount's merge logic).
                        //
                        // default_email_account lives on this object (not a bare
                        // $scope primitive) on purpose - its checkbox sits inside
                        // an ng-if="isEditing" block, which creates a child scope;
                        // a bare primitive ng-model there would write to that
                        // child scope only, never reaching save() below. Object
                        // properties don't have this problem (prototypal lookup
                        // finds the same shared object from any child scope).
                        $scope.account = isEditing
                            ? angular.copy({
                                email_address: existingAccount.email_address,
                                display_name: existingAccount.display_name,
                                default_email_account: !!existingAccount.default_email_account,
                                imap: { username: existingAccount.imap?.username || '', password: '' },
                                smtp: { username: existingAccount.smtp?.username || '', password: '' }
                            })
                            : {
                                email_address: '', display_name: '',
                                imap: { username: '', password: '' },
                                smtp: { username: '', password: '' }
                            };

                        $scope.errMessage = '';
                        $scope.fieldErrors = {};
                        $scope.saving = false;

                        // Same gh-user-selecting search-box-visibility sync
                        // trunk-numbers.tab.js's dialog relies on.
                        $scope.$watch('gudhubUserId', function (value) {
                            $scope.ghFieldStub.field_value = value || null;
                        });

                        $scope.testConnection = function () { return outerScope.testConnection(existingAccount); };
                        $scope.toggleSignatureEditor = function () { return outerScope.toggleSignatureEditor(existingAccount); };
                        $scope.saveSignature = function () { return outerScope.saveSignature(existingAccount); };

                        $scope.save = async function () {
                            const gudhubUserId = $scope.gudhubUserId ? String($scope.gudhubUserId).trim() : '';
                            const emailAddress = ($scope.account.email_address || '').trim();

                            // Recomputed on every attempt (not just accumulated) so
                            // fixing a field and re-clicking Save/Add clears its
                            // highlight even without a live per-field watch. A
                            // blank password is only an error when creating -
                            // when editing it just means "keep the current one".
                            $scope.fieldErrors = {
                                user: !gudhubUserId,
                                email: !emailAddress,
                                imapUsername: showCredentials && $scope.receivingEnabled && !$scope.account.imap.username,
                                imapPassword: showCredentials && $scope.receivingEnabled && !isEditing && !$scope.account.imap.password,
                                smtpUsername: showCredentials && $scope.sendingEnabled && !$scope.account.smtp.username,
                                smtpPassword: showCredentials && $scope.sendingEnabled && !isEditing && !$scope.account.smtp.password
                            };

                            if (!$scope.receivingEnabled && !$scope.sendingEnabled) {
                                $scope.errMessage = 'Enable at least one of receiving or sending';
                                return;
                            }

                            if (Object.values($scope.fieldErrors).some(Boolean)) {
                                $scope.errMessage = 'Please fill in the highlighted fields';
                                return;
                            }

                            $scope.errMessage = '';
                            $scope.saving = true;

                            const payload = {
                                gudhub_user_id: gudhubUserId,
                                email_address: emailAddress,
                                display_name: ($scope.account.display_name || '').trim() || emailAddress
                            };

                            if (showCredentials) {
                                payload.receiving_enabled = $scope.receivingEnabled;
                                payload.sending_enabled = $scope.sendingEnabled;
                                payload.imap = $scope.receivingEnabled ? $scope.account.imap : undefined;
                                payload.smtp = $scope.sendingEnabled ? $scope.account.smtp : undefined;
                            }

                            // Only ever sent as true - unsetting a default happens by
                            // making a different account default instead (see
                            // $scope.account's init comment, and EmailDatabase's
                            // _clearOtherDefaultAccountFlags).
                            if (isEditing && $scope.account.default_email_account) {
                                payload.default_email_account = true;
                            }

                            try {
                                if (isEditing) {
                                    await EmailSettingsService.updateAccount(existingAccount.account_id, payload);
                                } else {
                                    await EmailSettingsService.createAccount({ ...payload, provider_id: providerId });
                                }

                                $scope.hide();
                            } catch (error) {
                                console.log('Save account error:', error);
                                $scope.errMessage = error.message || 'Failed to save account';
                                $scope.saving = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                }).then(function () {
                    $scope.refreshAccounts();
                });
            };

            $scope.openAddAccountDialog = function () {
                if ($scope.provider.type === 'google') {
                    const url = EmailSettingsService.googleOAuthStartUrl(gudhub.storage.user.user_id, $scope.provider.provider_id);
                    window.open(url, 'gmail_oauth', 'width=520,height=650');
                    return;
                }

                if ($scope.provider.type === 'google_workspace') {
                    $scope.openAddWorkspaceAccountDialog();
                    return;
                }

                $scope.openAccountFormDialog(null);
            };

            // The "connect entire workspace, then just add users" flow: the
            // provider's own service account (domain-wide delegation)
            // authenticates on the picked user's behalf, so this needs no
            // credentials and no consent click from that user at all - just
            // which GudHub user + their address in the domain, same as
            // adding a DID number.
            $scope.openAddWorkspaceAccountDialog = function () {
                const providerId = $scope.provider.provider_id;

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="add_email_account-popup" style="padding: 20px; position: relative; min-width: 420px;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #333;">{{translate('Add Email Account')}}</h3>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('GudHub user')}}</label>
                                <div class="did_number-user_picker" ng-style="fieldErrors.user ? {'border': '1px solid #f44336', 'border-radius': '8px', 'padding': '4px'} : {}" style="margin-bottom: 16px;">
                                    <gh-user-selecting gh-model="gudhubUserId" gh-field="ghFieldStub" gh-mode="little_photo" gh-dropdown="" editable="true"></gh-user-selecting>
                                </div>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Email Address')}}</label>
                                <input type="email" ng-model="account.email_address" placeholder="{{translate('user')}}@{{translate('yourdomain.com')}}" ng-style="fieldErrors.email ? {'border-color': '#f44336', 'background': '#fff5f5'} : {}" style="width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 15px; outline: none; margin-bottom: 12px;">

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Display Name')}}</label>
                                <input type="text" ng-model="account.display_name" placeholder="{{translate('Optional')}}" style="width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid #d9e2ec; border-radius: 8px; font-size: 15px; outline: none; margin-bottom: 16px;">

                                <p style="font-size: 12px; color: #a4a4a8; margin-bottom: 16px;">{{translate('No credentials or sign-in needed - the workspace service account authenticates on this user\\'s behalf.')}}</p>

                                <div ng-show="errMessage" style="color: #f44336; font-size: 13px; font-weight: 600; margin-bottom: 12px; padding: 8px 10px; background: #fdecea; border-radius: 6px;">{{errMessage}}</div>

                                <div style="display: flex; gap: 10px; justify-content: center;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-blue" ng-click="save()" ng-disabled="saving">{{translate('Add')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: { providerId },
                    controller: ['$scope', 'providerId', 'localizationService', function ($scope, providerId, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);

                        $scope.gudhubUserId = '';
                        $scope.ghFieldStub = { data_model: { photo_size: '48px' } };
                        $scope.account = { email_address: '', display_name: '' };
                        $scope.errMessage = '';
                        $scope.fieldErrors = {};
                        $scope.saving = false;

                        $scope.$watch('gudhubUserId', function (value) {
                            $scope.ghFieldStub.field_value = value || null;
                        });

                        $scope.save = async function () {
                            const gudhubUserId = $scope.gudhubUserId ? String($scope.gudhubUserId).trim() : '';
                            const emailAddress = ($scope.account.email_address || '').trim();

                            $scope.fieldErrors = { user: !gudhubUserId, email: !emailAddress };

                            if (!gudhubUserId || !emailAddress) {
                                $scope.errMessage = 'Please fill in the highlighted fields';
                                return;
                            }

                            $scope.errMessage = '';
                            $scope.saving = true;

                            try {
                                await EmailSettingsService.createAccount({
                                    gudhub_user_id: gudhubUserId,
                                    provider_id: providerId,
                                    email_address: emailAddress,
                                    display_name: ($scope.account.display_name || '').trim() || emailAddress
                                });

                                $scope.hide();
                            } catch (error) {
                                console.log('Create account error:', error);
                                $scope.errMessage = error.message || 'Failed to add account';
                                $scope.saving = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                }).then(function () {
                    $scope.refreshAccounts();
                });
            };

            $scope.deleteAccount = function (account) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div style="padding: 20px; position: relative; min-width: 360px; text-align: center;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 12px; color: #333;">{{translate('Delete Account')}}</h3>
                                <p style="font-size: 14px; color: #666; margin-bottom: 20px;">{{translate('Are you sure you want to delete')}} <strong>{{emailAddress}}</strong>?</p>
                                <div style="display: flex; gap: 10px; justify-content: center;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-red" ng-click="confirm()" ng-disabled="deleting">{{translate('Delete')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: { emailAddress: account.email_address },
                    controller: ['$scope', 'emailAddress', 'localizationService', function ($scope, emailAddress, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);
                        $scope.emailAddress = emailAddress;
                        $scope.deleting = false;

                        $scope.confirm = async function () {
                            $scope.deleting = true;

                            try {
                                await EmailSettingsService.deleteAccount(account.account_id);
                                $scope.hide();
                            } catch (error) {
                                console.log('Delete account error:', error);
                                $scope.deleting = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                }).then(function () {
                    $scope.refreshAccounts();
                });
            };

            $scope.testConnection = async function (account) {
                account._testing = true;
                account._testResult = null;
                safeApply();

                try {
                    account._testResult = await EmailSettingsService.testConnection(account.account_id);
                } catch (error) {
                    account._testResult = { success: false, message: error.message };
                } finally {
                    account._testing = false;
                    safeApply();
                }
            };

            $scope.toggleSignatureEditor = function (account) {
                account._editingSignature = !account._editingSignature;
                account._signatureDraft = account.signature_html || '';
            };

            $scope.saveSignature = async function (account) {
                account._savingSignature = true;
                safeApply();

                try {
                    const updated = await EmailSettingsService.updateAccount(account.account_id, {
                        signature_html: account._signatureDraft
                    });
                    account.signature_html = updated.signature_html;
                    account._editingSignature = false;
                } catch (error) {
                    console.log('Save signature error:', error);
                } finally {
                    account._savingSignature = false;
                    safeApply();
                }
            };

            // The Connect-Gmail OAuth popup posts a message back to this
            // window and closes itself once linking completes (see
            // conversation-nodejs's /email/oauth/google/callback) - refresh
            // so the newly connected mailbox shows up without a reload.
            const onOAuthMessage = function (event) {
                if (!event.data || event.data.type !== 'gmail_connected') return;
                $scope.refreshAccounts();
            };

            window.addEventListener('message', onOAuthMessage);
            $scope.$on('$destroy', function () {
                window.removeEventListener('message', onOAuthMessage);
            });

            $scope.refreshAccounts();
        }],
        templateUrl: providerAccountsTemplateUrl
    };
}]);
